import { createPortal } from "react-dom";
import { ArrowRight, Check, Info, X } from "lucide-react";
import { useThemePortal } from "./channel-wizard-kit";
import { cn } from "./ui";
import { t } from "@/lib/i18n";

/* Выбор типа канала при подключении: у WhatsApp это обычный аккаунт и WABA,
   у Telegram — аккаунт по QR-коду и бот по токену. */

export type ChannelPick =
  | "whatsapp"
  | "waba"
  | "telegram"
  | "telegram-bot"
  | "viber"
  | "viber-bot"
  | "instagram"
  | "instagram-facebook";
export type PickerFamily = "whatsapp" | "telegram" | "viber" | "instagram";

type Option = {
  id: ChannelPick;
  icon: string;
  botMark?: boolean;
  title: string;
  heading: string;
  text: string;
  points: string[];
  badge?: string;
};

const PRESETS: Record<
  PickerFamily,
  { icon: string; title: string; intro: string; options: Option[] }
> = {
  whatsapp: {
    icon: "ri-whatsapp-line",
    title: t("Добавление канала"),
    intro:
      t("У WhatsApp два типа аккаунтов. Обычный подключается за минуту, официальный — открывает рассылки."),
    options: [
      {
        id: "whatsapp",
        icon: "ri-whatsapp-line",
        title: "WhatsApp",
        heading: t("Обычный WhatsApp"),
        text: t("Подключение любого аккаунта WhatsApp за минуту — с возможностью писать первым."),
        points: [t("Вход по QR-коду"), t("Или вход по номеру телефона")],
      },
      {
        id: "waba",
        icon: "ri-whatsapp-line",
        title: "WABA",
        heading: "WhatsApp Business API",
        text: t("Официальный способ подключить аккаунт WhatsApp Business — с возможностью делать рассылки."),
        points: [t("Рассылки по шаблонам"), t("Зелёная галочка и имя бренда")],
        badge: t("официальный"),
      },
    ],
  },
  telegram: {
    icon: "ri-telegram-line",
    title: t("Добавление канала"),
    intro:
      t("У Telegram тоже два варианта: любой аккаунт по QR-коду или бот по токену от @BotFather."),
    options: [
      {
        id: "telegram",
        icon: "ri-telegram-line",
        title: "Telegram",
        heading: t("Аккаунт Telegram"),
        text: t("Подключение любого аккаунта Telegram с возможностью писать первым."),
        points: [t("Вход по QR-коду"), t("История диалогов подтягивается")],
      },
      {
        id: "telegram-bot",
        icon: "ri-telegram-line",
        botMark: true,
        title: "Telegram Bot",
        heading: t("Telegram-бот"),
        text: t("Бот умеет общаться с клиентами, которые написали вам первыми."),
        points: [t("Токен от @BotFather"), t("Работает без телефона")],
        badge: t("бот"),
      },
    ],
  },
  viber: {
    icon: "ri-phone-line",
    title: t("Добавление канала"),
    intro:
      t("У Viber два пути: обычный аккаунт по QR-коду или официальный Viber Business по токену."),
    options: [
      {
        id: "viber",
        icon: "ri-phone-line",
        title: "Viber",
        heading: t("Аккаунт Viber"),
        text: t("Подключение любого аккаунта Viber за минуту — с возможностью писать первым."),
        points: [t("Вход по QR-коду"), t("История диалогов подтягивается")],
      },
      {
        id: "viber-bot",
        icon: "ri-phone-line",
        botMark: true,
        title: "Viber Business",
        heading: t("Официальный Viber Business"),
        text: t("Бот для рассылок и автоответов по токену из кабинета Viber Business."),
        points: [t("Рассылки по шаблонам"), t("Токен и имя отправителя")],
        badge: t("официальный"),
      },
    ],
  },
  instagram: {
    icon: "ri-instagram-line",
    title: t("Добавление канала"),
    intro:
      t("Instagram подключают двумя путями: напрямую аккаунтом или через бизнес-страницу Facebook."),
    options: [
      {
        id: "instagram",
        icon: "ri-instagram-line",
        title: "Instagram",
        heading: t("Аккаунт Instagram"),
        text: t("Вход прямо в аккаунт — личные сообщения и ответы на сторис."),
        points: [t("Логин, пароль и код 2FA"), t("Личные сообщения и сторис")],
      },
      {
        id: "instagram-facebook",
        icon: "ri-facebook-circle-line",
        title: t("Через Facebook"),
        heading: t("Instagram Business через Facebook"),
        text: t("Официальный путь: выбираете бизнес-страницу и разрешаете доступ к сообщениям."),
        points: [t("Выбор бизнес-страницы"), t("Права из Meta Business Suite")],
        badge: t("официальный"),
      },
    ],
  },
};

export default function ChannelPicker({
  family,
  onPick,
  onClose,
}: {
  family: PickerFamily;
  onPick: (kind: ChannelPick) => void;
  onClose: () => void;
}) {
  const host = useThemePortal();
  const preset = PRESETS[family];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4 md:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="channel-picker-title"
        className="flash-modal relative flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-t-card border border-border sm:rounded-card"
      >
        <div className="flex items-start gap-3 border-b border-border px-4 py-3.5 md:px-5">
          <span
            className="grid size-10 flex-none place-items-center rounded-btn bg-primary/10 text-xl leading-none text-primary"
            aria-hidden
          >
            <i className={preset.icon} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t("Каналы")}
            </p>
            <h3 id="channel-picker-title" className="mt-0.5 text-[15px] font-semibold leading-snug md:text-base">
              {preset.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть выбор типа канала")}
            className="grid size-8 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          <p className="text-[12px] leading-relaxed text-muted-foreground">{preset.intro}</p>

          <div className="mt-4 flex flex-col gap-3">
            {preset.options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onPick(o.id)}
                className="group grid cursor-pointer gap-3 rounded-card border border-border bg-card p-3 text-start transition hover:border-primary/50 hover:bg-primary/5 sm:grid-cols-[11rem_minmax(0,1fr)] sm:items-center sm:p-4"
              >
                <span className="flex flex-col items-center justify-center gap-2 rounded-btn border border-border bg-background px-3 py-4 text-center transition group-hover:border-primary/40">
                  <span className="relative inline-flex" aria-hidden>
                    <i className={cn(o.icon, "text-3xl leading-none text-primary")} />
                    {o.botMark ? (
                      <span className="absolute -bottom-1 -end-2 grid size-4 place-items-center rounded-full bg-primary text-[9px] leading-none text-primary-foreground">
                        <i className="ri-robot-2-line" />
                      </span>
                    ) : null}
                  </span>
                  <span className="text-[13px] font-semibold leading-none">{o.title}</span>
                </span>

                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-[12.5px] font-semibold">{o.heading}</span>
                    {o.badge ? (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                        {o.badge}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-1 block text-[12px] leading-relaxed text-muted-foreground">{o.text}</span>
                  <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {o.points.map((p) => (
                      <span
                        key={p}
                        className="inline-flex items-center gap-1 text-[11px] leading-relaxed text-foreground/80"
                      >
                        <Check className="size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                        {p}
                      </span>
                    ))}
                  </span>
                  <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
                    {t("Выбрать")}
                    <ArrowRight className="size-3 transition group-hover:translate-x-0.5" aria-hidden />
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2.5 md:px-5">
          <span className="flex items-start gap-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 size-3 flex-none" aria-hidden />
            {t("Демо-режим: подключение не затрагивает реальные аккаунты")}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 flex-none cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold text-muted-foreground transition hover:text-foreground"
          >
            {t("Отмена")}
          </button>
        </div>
      </div>
    </div>,
    host,
  );
}